/* eslint-disable max-lines-per-function */

import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { StyledFlex, TestableComponentProps } from '@dreryk/react-components';

import {
   getExtendedDosageAdministrationDaysCount,
   getExtendedDosagePackagesCount,
   getExtendedDosageTreatmentDaysCount,
   hasKnownPackageCapacity,
   isUnderTreatmentSupervision,
   useMedicineSufficiencyCount,
} from '~libs.shared.prescription';
import { MedicinePackage, Reimbursement, ReimbursementVariant } from '~libs.desktop.shared.medicine';

import { ActiveIngredientsQuantitiesTextValue } from '../../prescription/activeIngredients/ActiveIngredientsTextValue';
import { EntitlementTypesTextView } from '../../prescription/additionalEntitlements/EntitlementTypesTextView';
import { AdditionalRequirementsTextView } from '../../prescription/additionalRequirements/AdditionalRequirementsTextView';
import { DosageTextView } from '../../prescription/dosage/DosageTextView';
import { ExtendedDosagePackagesCountTextValue } from '../../prescription/extendedDosage/ExtendedDosagePackagesCountTextValue';
import { ExtendedDosageStagesTextView } from '../../prescription/extendedDosage/ExtendedDosageStagesTextView';
import { ExtendedDosageTreatmentDaysCountTextValue } from '../../prescription/extendedDosage/ExtendedDosageTreatmentDaysCountTextValue';
import { IssuedFromTextView } from '../../prescription/issuedDate/IssuedFromTextView';
import { IssuedToTextView } from '../../prescription/issuedDate/IssuedToTextView';
import { MedicineHeading } from '../../prescription/MedicineHeading';
import { MedicineSufficientForTextValue } from '../../prescription/numericalDosage/MedicineSufficientForTextValue';
import { NumericalDosageTextView } from '../../prescription/numericalDosage/NumericalDosageTextView';
import { PrescriptionCard } from '../../prescription/PrescriptionCard';
import { PrescriptionCategoryTextView } from '../../prescription/prescriptionCategory/PrescriptionCategoryTextView';
import { QuantityTextView } from '../../prescription/quantity/QuantityTextView';
import { PrescriptionStatusBox } from '../../prescription/status/PrescriptionStatusBoxPortal';
import { TreatmentDaysCountTextView } from '../../prescription/treatmentDaysCount/TreatmentDaysCountTextView';
import { SentPrescriptionAnnulmentLink } from '../common/SentPrescriptionAnnulmentLink';
import { SentPrescriptionStatus } from '../SentPrescriptionStatus';

import { SentStandardPrescriptionGrid } from './SentStandardPrescriptionGrid';
import { SentStandardPrescription } from './SentStandardPrescriptionItem.types';

type SentStandardPrescriptionItemProps = TestableComponentProps &
   SentStandardPrescription & {
      readonly visitId?: number;
      readonly visitDiagnoses: VisitDiagnosis[];
   };

export const SentStandardPrescriptionItem = (props: SentStandardPrescriptionItemProps): JSX.Element => {
   const patientMedicine = props.medicinePrescription.patientMedicine!;
   const underTreatmentSupervision = isUnderTreatmentSupervision({
      issuedForYear: props.additionalRequirementTypes.includes('FOR_YEAR'),
      reimbursement: props.reimbursement,
      packageCapacityKnown: hasKnownPackageCapacity(patientMedicine.medicine),
   });
   const medicineSufficiencyCount = useMedicineSufficiencyCount({
      medicine: patientMedicine.medicine,
      preciseDosage: props.preciseDosage,
      packagesCount: props.quantity,
   });

   const extendedDosageActive = Boolean(props.extendedDosage?.stages.length);
   const extendedDosagePackagesCount = extendedDosageActive
      ? getExtendedDosagePackagesCount(props.extendedDosage!, patientMedicine.medicine)
      : undefined;
   const extendedDosageTreatmentDaysCount = extendedDosageActive
      ? getExtendedDosageTreatmentDaysCount(props.extendedDosage!)
      : undefined;
   const extendedDosageAdministrationDaysCount = extendedDosageActive
      ? getExtendedDosageAdministrationDaysCount(props.extendedDosage!)
      : undefined;
   const displayedQuantity = extendedDosageActive ? extendedDosagePackagesCount ?? props.quantity : props.quantity;
   return (
      <PrescriptionCard
         testId={props.testId}
         heading={
            <MedicineHeading
               medicine={patientMedicine.medicine}
               reimbursements={patientMedicine.reimbursements}
               visitDiagnoses={props.visitDiagnoses}
            />
         }
         subheading={
            <MedicinePackage
               dose={patientMedicine.medicine.dose}
               form={patientMedicine.medicine.form}
               packageContent={patientMedicine.medicine.packageContent}
            />
         }
         annulled={props.annulled ? <SentPrescriptionStatus /> : null}
         options={
            props.annulled ? null : (
               <SentPrescriptionAnnulmentLink
                  disabled={!props.annullable}
                  prescriptionId={props.id}
                  visitId={props.visitId!}
               />
            )
         }
      >
         <PrescriptionStatusBox prescriptionId={props.id} />

         <StyledFlex direction={'column'} gap={'8px'}>
            {props.reimbursement ? (
               <Reimbursement
                  code={props.reimbursement.code}
                  priceDescription={props.reimbursement.priceDescription}
                  variant={ReimbursementVariant.STANDARD}
                  active
                  disabled
               />
            ) : null}

            <SentStandardPrescriptionGrid
               dosage={
                  <DosageTextView
                     dosage={
                        extendedDosageActive ? props.extendedDosage!.patientInstruction ?? props.dosage : props.dosage
                     }
                  />
               }
               issuedFrom={<IssuedFromTextView issuedFrom={props.issuedFrom} />}
               quantity={
                  extendedDosageActive ? (
                     <ExtendedDosagePackagesCountTextValue packagesCount={displayedQuantity} />
                  ) : (
                     <QuantityTextView quantity={props.quantity} />
                  )
               }
               issuedTo={<IssuedToTextView issuedTo={props.issuedTo} />}
               activeIngredients={
                  <ActiveIngredientsQuantitiesTextValue
                     medicinePrescription={props.medicinePrescription}
                     quantity={displayedQuantity}
                  />
               }
               medicineSufficientFor={
                  <MedicineSufficientForTextValue
                     medicineSufficiencyCount={
                        extendedDosageActive ? extendedDosageAdministrationDaysCount : medicineSufficiencyCount
                     }
                  />
               }
               additionalEntitlementTypes={
                  <EntitlementTypesTextView entitlementTypes={props.additionalEntitlementTypes} />
               }
               additionalRequirementTypes={
                  <AdditionalRequirementsTextView additionalRequirementTypes={props.additionalRequirementTypes} />
               }
               numericalDosage={
                  extendedDosageActive ? (
                     <ExtendedDosageStagesTextView
                        stages={props.extendedDosage!.stages}
                        portionUnit={props.extendedDosage!.portionUnit ?? null}
                     />
                  ) : (
                     <NumericalDosageTextView preciseDosage={props.preciseDosage} />
                  )
               }
               treatmentDaysCount={
                  extendedDosageActive ? (
                     <ExtendedDosageTreatmentDaysCountTextValue treatmentDaysCount={extendedDosageTreatmentDaysCount} />
                  ) : (
                     <TreatmentDaysCountTextView
                        treatmentDaysCount={props.treatmentDaysCount}
                        underTreatmentSupervision={underTreatmentSupervision}
                     />
                  )
               }
               prescriptionCategory={
                  props.prescriptionCategory ? (
                     <PrescriptionCategoryTextView prescriptionCategory={props.prescriptionCategory} />
                  ) : undefined
               }
            />
         </StyledFlex>
      </PrescriptionCard>
   );
};
